import { z } from "zod"

import { entityIdSchema } from "./identifiers.js"

const instantSchema = z.iso.datetime({ offset: true })

export const locationPolicyConfigureV1Schema = z.object({
  policyId: entityIdSchema,
  mode: z.enum(["Operational Events", "Continuous On Shift"]),
  intervalMinutes: z.number().int().min(5).max(120),
  retentionDays: z.number().int().min(1).max(365),
}).strict()

const locationEvidenceV1Fields = {
  evidenceId: entityIdSchema,
  policyId: entityIdSchema,
  workShiftId: entityIdSchema,
  operationalEventId: entityIdSchema.nullable(),
  kind: z.enum(["Operational Event", "Interval"]),
  status: z.enum(["Captured", "Unavailable"]),
  capturedAt: instantSchema,
  latitude: z.number().min(-90).max(90).nullable(),
  longitude: z.number().min(-180).max(180).nullable(),
  accuracyMeters: z.number().positive().max(10_000).nullable(),
  source: z.enum(["native", "network", "unavailable"]),
  exceptionReason: z.string().trim().min(1).max(500).nullable(),
}

const validateLegacyEvidence = (
  value: {
    kind: "Operational Event" | "Interval"
    operationalEventId: string | null
    status: "Captured" | "Unavailable"
    latitude: number | null
    longitude: number | null
    accuracyMeters: number | null
    exceptionReason: string | null
  },
  context: z.RefinementCtx,
): void => {
  const coordinates = [value.latitude, value.longitude, value.accuracyMeters]
  if ((value.kind === "Operational Event") !== (value.operationalEventId !== null)) {
    context.addIssue({ code: "custom", path: ["operationalEventId"], message: "Event binding mismatch." })
  }
  if (value.status === "Captured" && coordinates.some((item) => item === null)) {
    context.addIssue({ code: "custom", path: ["status"], message: "Coordinates required." })
  }
  if (value.status === "Unavailable" &&
      (coordinates.some((item) => item !== null) || value.exceptionReason === null)) {
    context.addIssue({ code: "custom", path: ["status"], message: "Reason required." })
  }
}

export const locationEvidenceRecordV1Schema = z
  .object(locationEvidenceV1Fields)
  .strict()
  .superRefine(validateLegacyEvidence)

export const locationEvidenceRecordV2Schema = z.object({
  ...locationEvidenceV1Fields,
  serviceState: z.enum([
    "Available",
    "Permission Denied",
    "Services Disabled",
    "Provider Error",
  ]),
  deviceIntegrity: z.enum(["No Mock Signal", "Mocked", "Unknown"]),
}).strict().superRefine((value, context) => {
  validateLegacyEvidence(value, context)
  const coordinates = [value.latitude, value.longitude, value.accuracyMeters]
  if (
    value.status === "Captured" &&
    (value.serviceState !== "Available" ||
      value.source === "unavailable" ||
      value.exceptionReason !== null)
  ) {
    context.addIssue({
      code: "custom",
      path: ["serviceState"],
      message: "Captured evidence requires an available provider.",
    })
  }
  if (value.status === "Unavailable" &&
      (coordinates.some((item) => item !== null) ||
        value.source !== "unavailable" ||
        value.serviceState === "Available" ||
        value.deviceIntegrity !== "Unknown" ||
        value.exceptionReason === null)) {
    context.addIssue({ code: "custom", path: ["status"], message: "Reason required." })
  }
})

export const locationHistoryReviewV1Schema = z.object({
  reviewId: entityIdSchema,
  workShiftId: entityIdSchema,
  reason: z.enum([
    "time_exception",
    "customer_dispute",
    "safety_investigation",
    "dispatch_review",
  ]),
  limit: z.number().int().min(1).max(100),
}).strict()

export type LocationPolicyConfigureV1 = z.infer<typeof locationPolicyConfigureV1Schema>
export type LocationEvidenceRecordV1 = z.infer<typeof locationEvidenceRecordV1Schema>
export type LocationEvidenceRecordV2 = z.infer<typeof locationEvidenceRecordV2Schema>
export type LocationHistoryReviewV1 = z.infer<typeof locationHistoryReviewV1Schema>
